import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import AppConfig from '@/config'
import { useCookiesStore } from '@/stores/cookies'
import { isIOS, isMacOS, isSafari, isStandalone } from '@/utils/deviceDetection'
import { STORAGE_KEYS } from '@/constants/storageKeys'

const DISMISSED_AT_KEY = STORAGE_KEYS.PWA_DISMISSED_AT
const INSTALLED_KEY = STORAGE_KEYS.PWA_INSTALLED
const DISMISS_PERIOD_DAYS = 30

// Какой вариант pop-up показать:
// 'native'    — Android/desktop, стандартная установка кнопкой «Добавить»
// 'iosSafari' — iOS Safari, прямая инструкция (нажать share → «На экран Домой»)
// 'iosOther'  — iOS вне Safari (Chrome и т.п.), сначала открыть сайт в Safari
export type PwaInstallMode = 'native' | 'iosSafari' | 'iosOther'

export const usePwaStore = defineStore('pwa', () => {
	const showInstallPrompt = ref(false)
	const installMode = ref<PwaInstallMode | null>(null)
	// shallowRef, чтобы Vue не оборачивал нативное событие в reactive-прокси —
	// иначе .prompt() вызывается с this = Proxy и падает с Illegal invocation
	// (наблюдалось в Яндекс.Браузере на десктопе).
	const deferredPrompt = shallowRef<BeforeInstallPromptEvent | null>(null)

	/** Сохранить отложенное событие beforeinstallprompt (Android/desktop). */
	function setDeferredPrompt(event: BeforeInstallPromptEvent) {
		deferredPrompt.value = event
	}

	/** Не отклонял ли пользователь предложение в течение последних 30 дней. */
	function isDismissedRecently(): boolean {
		const dismissedAt = localStorage.getItem(DISMISSED_AT_KEY)
		if (!dismissedAt) return false
		const dismissedTime = new Date(dismissedAt).getTime()
		if (Number.isNaN(dismissedTime)) return false
		const daysPassed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
		return daysPassed < DISMISS_PERIOD_DAYS
	}

	/** Cookie-баннер закрыт/принят либо не применяется для данного тенанта. */
	function isCookiePromptResolved(): boolean {
		const cookiesStore = useCookiesStore()
		if (cookiesStore.showCookiesDialog) return false
		// Баннер cookie показывается только для локали BY. Для остальных — условие выполнено сразу.
		if (AppConfig.local !== 'BY') return true
		return localStorage.getItem(STORAGE_KEYS.COOKIES_DIALOG_SHOWN) === 'true'
	}

	/**
	 * Показать pop-up предложения установки, только если выполнены ВСЕ условия:
	 * авторизация, не установлено, не отклоняли < 30 дней, cookie-баннер закрыт,
	 * и (Android/desktop) браузер прислал beforeinstallprompt ИЛИ (iOS Safari).
	 */
	function maybeShow(isAuthenticated: boolean) {
		if (!AppConfig.pwaEnabled) return
		if (showInstallPrompt.value) return
		if (!isAuthenticated) return
		if (isStandalone() || localStorage.getItem(INSTALLED_KEY) === 'true') return
		if (isDismissedRecently()) return
		if (!isCookiePromptResolved()) return

		if (deferredPrompt.value) {
			installMode.value = 'native'
			showInstallPrompt.value = true
			return
		}

		// На iOS нет beforeinstallprompt — показываем инструкцию.
		// В Safari — прямые шаги, в других браузерах — предложение открыть в Safari.
		if (isIOS()) {
			installMode.value = isSafari() ? 'iosSafari' : 'iosOther'
			showInstallPrompt.value = true
			return
		}

		// Десктопный Safari на macOS тоже не шлёт beforeinstallprompt —
		// показываем существующую инструкцию (режим iosOther).
		if (isMacOS() && isSafari()) {
			installMode.value = 'iosOther'
			showInstallPrompt.value = true
		}
	}

	/** Запустить стандартный сценарий установки (Android/desktop). */
	async function triggerInstall() {
		const promptEvent = deferredPrompt.value
		showInstallPrompt.value = false
		if (!promptEvent) return
		deferredPrompt.value = null
		try {
			await promptEvent.prompt()
			const { outcome } = await promptEvent.userChoice
			// 'accepted' отслеживается отдельно через событие 'appinstalled'.
			if (outcome === 'dismissed') markDismissed()
		} catch (e) {
			// Например, Illegal invocation в некоторых браузерах — не даём
			// попытке пропасть бесследно, иначе pop-up будет появляться заново.
			console.error(e)
			markDismissed()
		}
	}

	/** Пользователь закрыл pop-up без установки — не показывать ещё 30 дней. */
	function markDismissed() {
		localStorage.setItem(DISMISSED_AT_KEY, new Date().toISOString())
		showInstallPrompt.value = false
	}

	/** Приложение установлено — больше не предлагать никогда. */
	function markInstalled() {
		localStorage.setItem(INSTALLED_KEY, 'true')
		deferredPrompt.value = null
		showInstallPrompt.value = false
	}

	return {
		showInstallPrompt,
		installMode,
		deferredPrompt,
		setDeferredPrompt,
		maybeShow,
		triggerInstall,
		markDismissed,
		markInstalled
	}
})
